// =============================================================================
// Impressum — structured legal content, EN canonical / DE / FR / ES / RU.
//
// English is canonical (mirrors IMPRESSUM.md in docs/legal/ of the parent triptribe repo, exactly).
// Translations must be updated whenever the English source changes.
//
// German statutory abbreviations (DDG, MStV, VSBG, TMG) are never
// translated in any language — they're citations to specific named German
// statutes, not generic concepts; every language keeps the German
// abbreviation and translates only the surrounding descriptive text.
// Factual identifiers (name, address, email, URLs) are never translated.
// =============================================================================

import type { LegalContentByLanguage } from './types';

export const impressumContent: LegalContentByLanguage = {
  en: {
    title: 'Impressum',
    subtitle: 'Legal notice pursuant to § 5 DDG (the German Digital Services Act) and, where applicable, § 18(2) MStV.',
    iconName: 'file-text',
    sections: [
      {
        title: 'Service Provider',
        iconName: 'user',
        paragraphs: ['Akshat Mohiley — Hildebrandstraße 7, Apartment 906, 01219 Dresden, Germany.'],
      },
      {
        title: 'Contact',
        iconName: 'mail',
        paragraphs: ['Website: https://tonyte.app', 'Email: hello@tonyte.app'],
      },
      {
        title: 'Responsible for Content (§ 18(2) MStV)',
        iconName: 'file-text',
        paragraphs: [
          'For any journalistic-editorial content on the Tonyte website (including its blog section), the person responsible within the meaning of § 18(2) MStV is:',
          'Akshat Mohiley (address as above).',
        ],
      },
      { title: 'Commercial Register', iconName: 'briefcase', paragraphs: ['Not registered.'] },
      { title: 'VAT Identification Number', iconName: 'hash', paragraphs: ['None.'] },
      {
        title: 'Regulatory / Supervisory Body',
        iconName: 'shield',
        paragraphs: ['Tonyte is not currently subject to any professional licensing or regulatory regime requiring disclosure of a supervisory authority.'],
      },
      {
        title: 'Dispute Resolution',
        iconName: 'message-circle',
        paragraphs: [
          "The European Commission's Online Dispute Resolution (ODR) platform ceased operation on 20 July 2025 and is not referenced here.",
          'Tonyte is not obligated to state its willingness to participate in consumer arbitration board proceedings under § 36 VSBG (German Consumer Dispute Resolution Act), as this obligation applies only to businesses that generally employ more than ten people.',
        ],
      },
      {
        title: 'Liability for Content',
        iconName: 'alert-triangle',
        paragraphs: [
          "As a service provider, Akshat Mohiley is responsible for Tonyte's own content on this website and app in accordance with general law (§ 7(1) DDG). Under §§ 8–10 DDG, however, there is no obligation to monitor transmitted or stored third-party (user-generated) information, or to actively investigate circumstances indicating illegal activity. Obligations to remove or block the use of information under general law remain unaffected; liability in this respect is only possible from the point in time at which a specific legal infringement becomes known. Upon becoming aware of any such infringement, this content will be removed promptly.",
        ],
      },
      {
        title: 'Liability for Links',
        iconName: 'share-2',
        paragraphs: [
          "Tonyte's Services may contain links to external third-party websites (see the Privacy Policy, Section 4, for the third-party services actually integrated). Their content is outside Akshat Mohiley's control, and no liability is assumed for it. The respective provider or operator of a linked page is always responsible for its content. Linked pages were checked for possible legal violations at the time of linking; no illegal content was identifiable at that time. Continuous monitoring of linked pages' content is not reasonable without concrete evidence of a legal violation. Upon becoming aware of any such infringement, the affected link will be removed promptly.",
        ],
      },
      {
        title: 'Copyright',
        iconName: 'scroll',
        paragraphs: [
          'Content created or made available through Tonyte on this website and app is subject to German copyright law. Reproduction, editing, distribution, or any form of use outside the limits of copyright law requires prior written consent. Downloads and copies of this content for private, non-commercial use are permitted. Insofar as content on this site was not created by the operator, third-party copyrights are respected; user-generated content is addressed separately in the Terms of Service (User-Generated Content section).',
        ],
      },
      {
        title: 'Note on Scope',
        iconName: 'info',
        paragraphs: [
          'This Impressum applies to the Tonyte website (tonyte.app and subpages), the Tonyte mobile applications, and the Partner Dashboard, consistent with the Services defined in the Privacy Policy, Section 2.',
        ],
      },
    ],
  },
  de: {
    title: 'Impressum',
    subtitle: 'Impressum gemäß § 5 DDG (Digitale-Dienste-Gesetz) und, soweit einschlägig, § 18(2) MStV.',
    iconName: 'file-text',
    sections: [
      {
        title: 'Diensteanbieter',
        iconName: 'user',
        paragraphs: ['Akshat Mohiley — Hildebrandstraße 7, Apartment 906, 01219 Dresden, Deutschland.'],
      },
      {
        title: 'Kontakt',
        iconName: 'mail',
        paragraphs: ['Website: https://tonyte.app', 'E-Mail: hello@tonyte.app'],
      },
      {
        title: 'Verantwortlich für den Inhalt (§ 18(2) MStV)',
        iconName: 'file-text',
        paragraphs: [
          'Verantwortlich im Sinne des § 18(2) MStV für journalistisch-redaktionelle Inhalte auf der Tonyte-Website (einschließlich des Blog-Bereichs) ist:',
          'Akshat Mohiley (Anschrift wie oben).',
        ],
      },
      { title: 'Handelsregister', iconName: 'briefcase', paragraphs: ['Nicht eingetragen.'] },
      { title: 'Umsatzsteuer-Identifikationsnummer', iconName: 'hash', paragraphs: ['Keine.'] },
      {
        title: 'Aufsichtsbehörde',
        iconName: 'shield',
        paragraphs: ['Tonyte unterliegt derzeit keiner berufsrechtlichen Zulassung oder Aufsicht, die die Angabe einer Aufsichtsbehörde erfordern würde.'],
      },
      {
        title: 'Streitbeilegung',
        iconName: 'message-circle',
        paragraphs: [
          'Die Plattform der Europäischen Kommission zur Online-Streitbeilegung (OS-Plattform) hat ihren Betrieb am 20. Juli 2025 eingestellt und wird hier nicht referenziert.',
          'Tonyte ist nicht verpflichtet, seine Bereitschaft zur Teilnahme an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle nach § 36 VSBG (Verbraucherstreitbeilegungsgesetz) zu erklären, da diese Pflicht nur Unternehmen mit in der Regel mehr als zehn Beschäftigten trifft.',
        ],
      },
      {
        title: 'Haftung für Inhalte',
        iconName: 'alert-triangle',
        paragraphs: [
          'Als Diensteanbieter ist Akshat Mohiley gemäß § 7(1) DDG für eigene Inhalte von Tonyte auf dieser Website und in der App nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8–10 DDG ist er jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde (nutzergenerierte) Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt; eine diesbezügliche Haftung ist erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden entsprechender Rechtsverletzungen werden die betreffenden Inhalte umgehend entfernt.',
        ],
      },
      {
        title: 'Haftung für Links',
        iconName: 'share-2',
        paragraphs: [
          "Die Services von Tonyte können Links zu externen Websites Dritter enthalten (siehe Datenschutzerklärung, Abschnitt 4, für die tatsächlich eingebundenen Drittanbieterdienste). Auf deren Inhalte hat Akshat Mohiley keinen Einfluss; eine Haftung dafür wird nicht übernommen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seite verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft; rechtswidrige Inhalte waren zu diesem Zeitpunkt nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen wird der betreffende Link umgehend entfernt.",
        ],
      },
      {
        title: 'Urheberrecht',
        iconName: 'scroll',
        paragraphs: [
          'Die durch Tonyte auf dieser Website und in der App erstellten oder bereitgestellten Inhalte unterliegen dem deutschen Urheberrecht. Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechts bedürfen der vorherigen schriftlichen Zustimmung. Downloads und Kopien dieser Inhalte für den privaten, nicht kommerziellen Gebrauch sind gestattet. Soweit Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet; nutzergenerierte Inhalte werden gesondert in den Allgemeinen Geschäftsbedingungen (Abschnitt „Nutzergenerierte Inhalte“) behandelt.',
        ],
      },
      {
        title: 'Hinweis zum Geltungsbereich',
        iconName: 'info',
        paragraphs: [
          'Dieses Impressum gilt für die Tonyte-Website (tonyte.app und Unterseiten), die mobilen Tonyte-Anwendungen und das Partner Dashboard, entsprechend der Definition der Services in der Datenschutzerklärung, Abschnitt 2.',
        ],
      },
    ],
  },
  fr: {
    title: 'Impressum',
    subtitle: "Mentions légales conformément au § 5 DDG (loi allemande sur les services numériques) et, le cas échéant, au § 18(2) MStV.",
    iconName: 'file-text',
    sections: [
      {
        title: 'Prestataire de services',
        iconName: 'user',
        paragraphs: ['Akshat Mohiley — Hildebrandstraße 7, Apartment 906, 01219 Dresde, Allemagne.'],
      },
      {
        title: 'Contact',
        iconName: 'mail',
        paragraphs: ['Site web : https://tonyte.app', 'E-mail : hello@tonyte.app'],
      },
      {
        title: 'Responsable du contenu (§ 18(2) MStV)',
        iconName: 'file-text',
        paragraphs: [
          "Pour tout contenu journalistique-éditorial publié sur le site web de Tonyte (y compris sa section blog), la personne responsable au sens du § 18(2) MStV est :",
          'Akshat Mohiley (adresse ci-dessus).',
        ],
      },
      { title: 'Registre du commerce', iconName: 'briefcase', paragraphs: ["Non immatriculé."] },
      { title: "Numéro d'identification TVA", iconName: 'hash', paragraphs: ['Aucun.'] },
      {
        title: 'Autorité de régulation / de surveillance',
        iconName: 'shield',
        paragraphs: ["Tonyte n'est actuellement soumis à aucun régime d'agrément professionnel ou de surveillance nécessitant la mention d'une autorité de tutelle."],
      },
      {
        title: 'Règlement des litiges',
        iconName: 'message-circle',
        paragraphs: [
          "La plateforme de résolution des litiges en ligne (RLL) de la Commission européenne a cessé son activité le 20 juillet 2025 et n'est pas référencée ici.",
          "Tonyte n'est pas tenu de déclarer sa disposition à participer à une procédure devant une commission de conciliation des consommateurs au titre du § 36 VSBG (loi allemande sur le règlement des litiges de consommation), cette obligation ne s'appliquant qu'aux entreprises employant généralement plus de dix personnes.",
        ],
      },
      {
        title: 'Responsabilité du contenu',
        iconName: 'alert-triangle',
        paragraphs: [
          "En tant que prestataire de services, Akshat Mohiley est responsable de son propre contenu Tonyte sur ce site et cette application conformément au droit commun (§ 7(1) DDG). En vertu des §§ 8 à 10 DDG, il n'est toutefois pas tenu de surveiller les informations de tiers (générées par les utilisateurs) transmises ou stockées, ni de rechercher activement des circonstances indiquant une activité illicite. Les obligations de suppression ou de blocage de l'utilisation d'informations en vertu du droit commun restent inchangées ; une responsabilité à cet égard n'est possible qu'à compter du moment où une violation légale concrète est portée à sa connaissance. Dès connaissance d'une telle violation, le contenu concerné sera retiré sans délai.",
        ],
      },
      {
        title: 'Responsabilité des liens',
        iconName: 'share-2',
        paragraphs: [
          "Les Services de Tonyte peuvent contenir des liens vers des sites web tiers externes (voir la Politique de confidentialité, Section 4, pour les services tiers réellement intégrés). Leur contenu échappe au contrôle d'Akshat Mohiley, et aucune responsabilité n'est assumée à cet égard. Le fournisseur ou exploitant respectif d'une page liée est toujours responsable de son contenu. Les pages liées ont été vérifiées, au moment de leur mise en lien, quant à d'éventuelles violations légales ; aucun contenu illicite n'était identifiable à ce moment-là. Une surveillance permanente du contenu des pages liées n'est pas raisonnablement exigible en l'absence d'indices concrets d'une violation légale. Dès connaissance d'une telle violation, le lien concerné sera retiré sans délai.",
        ],
      },
      {
        title: "Droits d'auteur",
        iconName: 'scroll',
        paragraphs: [
          "Le contenu créé ou mis à disposition par Tonyte sur ce site et cette application est soumis au droit d'auteur allemand. Toute reproduction, modification, distribution ou autre forme d'exploitation dépassant les limites du droit d'auteur nécessite l'accord écrit préalable. Le téléchargement et la copie de ce contenu pour un usage privé, non commercial, sont autorisés. Dans la mesure où des contenus de ce site n'ont pas été créés par l'exploitant, les droits d'auteur des tiers sont respectés ; le contenu généré par les utilisateurs est traité séparément dans les Conditions d'Utilisation (section Contenu Généré par l'Utilisateur).",
        ],
      },
      {
        title: 'Note sur le champ d’application',
        iconName: 'info',
        paragraphs: [
          "Cet Impressum s'applique au site web de Tonyte (tonyte.app et ses sous-pages), aux applications mobiles Tonyte et au Partner Dashboard, conformément aux Services définis dans la Politique de confidentialité, Section 2.",
        ],
      },
    ],
  },
  es: {
    title: 'Impressum',
    subtitle: 'Aviso legal conforme al § 5 DDG (ley alemana de servicios digitales) y, cuando proceda, al § 18(2) MStV.',
    iconName: 'file-text',
    sections: [
      {
        title: 'Prestador del servicio',
        iconName: 'user',
        paragraphs: ['Akshat Mohiley — Hildebrandstraße 7, Apartment 906, 01219 Dresde, Alemania.'],
      },
      {
        title: 'Contacto',
        iconName: 'mail',
        paragraphs: ['Sitio web: https://tonyte.app', 'Correo electrónico: hello@tonyte.app'],
      },
      {
        title: 'Responsable del contenido (§ 18(2) MStV)',
        iconName: 'file-text',
        paragraphs: [
          'La persona responsable, en el sentido del § 18(2) MStV, de cualquier contenido periodístico-editorial en el sitio web de Tonyte (incluida su sección de blog) es:',
          'Akshat Mohiley (dirección arriba indicada).',
        ],
      },
      { title: 'Registro Mercantil', iconName: 'briefcase', paragraphs: ['No inscrito.'] },
      { title: 'Número de identificación de IVA', iconName: 'hash', paragraphs: ['Ninguno.'] },
      {
        title: 'Autoridad reguladora / supervisora',
        iconName: 'shield',
        paragraphs: ['Tonyte no está actualmente sujeto a ningún régimen de autorización profesional o supervisión regulatoria que requiera la indicación de una autoridad supervisora.'],
      },
      {
        title: 'Resolución de litigios',
        iconName: 'message-circle',
        paragraphs: [
          'La plataforma de resolución de litigios en línea (ODR) de la Comisión Europea cesó su actividad el 20 de julio de 2025 y no se hace referencia a ella aquí.',
          'Tonyte no está obligado a declarar su disposición a participar en procedimientos ante una junta de arbitraje de consumidores conforme al § 36 VSBG (ley alemana de resolución de litigios de consumo), ya que esta obligación solo se aplica a empresas que habitualmente emplean a más de diez personas.',
        ],
      },
      {
        title: 'Responsabilidad por el contenido',
        iconName: 'alert-triangle',
        paragraphs: [
          'Como prestador del servicio, Akshat Mohiley es responsable de los contenidos propios de Tonyte en este sitio web y aplicación conforme a la legislación general (§ 7(1) DDG). No obstante, en virtud de los §§ 8 a 10 DDG, no existe obligación de supervisar la información de terceros (generada por usuarios) transmitida o almacenada, ni de investigar activamente circunstancias que indiquen actividad ilícita. Las obligaciones de retirar o bloquear el uso de información conforme a la legislación general permanecen inalteradas; la responsabilidad al respecto solo es posible a partir del momento en que se tenga conocimiento de una infracción legal concreta. En cuanto se tenga conocimiento de tal infracción, el contenido correspondiente será retirado sin demora.',
        ],
      },
      {
        title: 'Responsabilidad por enlaces',
        iconName: 'share-2',
        paragraphs: [
          "Los Servicios de Tonyte pueden contener enlaces a sitios web externos de terceros (véase la Política de Privacidad, Sección 4, para los servicios de terceros efectivamente integrados). Su contenido escapa al control de Akshat Mohiley y no se asume responsabilidad alguna por él. El respectivo proveedor u operador de una página enlazada es siempre responsable de su contenido. Las páginas enlazadas fueron revisadas, en el momento de establecer el enlace, en busca de posibles infracciones legales; en ese momento no se identificó contenido ilícito. No es razonable exigir una supervisión continua del contenido de las páginas enlazadas sin indicios concretos de una infracción legal. En cuanto se tenga conocimiento de tal infracción, el enlace correspondiente será retirado sin demora.",
        ],
      },
      {
        title: 'Derechos de autor',
        iconName: 'scroll',
        paragraphs: [
          'El contenido creado o puesto a disposición a través de Tonyte en este sitio web y aplicación está sujeto a la legislación alemana de derechos de autor. La reproducción, edición, distribución o cualquier forma de uso fuera de los límites de la legislación de derechos de autor requiere el consentimiento previo por escrito. Se permiten las descargas y copias de este contenido para uso privado y no comercial. En la medida en que el contenido de este sitio no haya sido creado por el operador, se respetan los derechos de autor de terceros; el contenido generado por el usuario se trata por separado en los Términos de Servicio (sección de Contenido Generado por el Usuario).',
        ],
      },
      {
        title: 'Nota sobre el ámbito de aplicación',
        iconName: 'info',
        paragraphs: [
          'Este Impressum se aplica al sitio web de Tonyte (tonyte.app y subpáginas), a las aplicaciones móviles de Tonyte y al Partner Dashboard, en consonancia con los Servicios definidos en la Política de Privacidad, Sección 2.',
        ],
      },
    ],
  },
  ru: {
    title: 'Impressum',
    subtitle: 'Правовая информация в соответствии с § 5 DDG (немецкого Закона о цифровых услугах) и, при необходимости, § 18(2) MStV.',
    iconName: 'file-text',
    sections: [
      {
        title: 'Поставщик услуг',
        iconName: 'user',
        paragraphs: ['Akshat Mohiley — Hildebrandstraße 7, Apartment 906, 01219 Dresden, Германия.'],
      },
      {
        title: 'Контакты',
        iconName: 'mail',
        paragraphs: ['Сайт: https://tonyte.app', 'Эл. почта: hello@tonyte.app'],
      },
      {
        title: 'Ответственный за содержание (§ 18(2) MStV)',
        iconName: 'file-text',
        paragraphs: [
          'Лицом, ответственным по смыслу § 18(2) MStV за журналистско-редакционный контент на сайте Tonyte (включая раздел блога), является:',
          'Akshat Mohiley (адрес указан выше).',
        ],
      },
      { title: 'Торговый реестр', iconName: 'briefcase', paragraphs: ['Не зарегистрировано.'] },
      { title: 'Идентификационный номер плательщика НДС', iconName: 'hash', paragraphs: ['Отсутствует.'] },
      {
        title: 'Регулирующий / надзорный орган',
        iconName: 'shield',
        paragraphs: ['В настоящее время Tonyte не подпадает под какой-либо режим профессионального лицензирования или регулирования, требующий указания надзорного органа.'],
      },
      {
        title: 'Разрешение споров',
        iconName: 'message-circle',
        paragraphs: [
          'Платформа Европейской комиссии по урегулированию споров в режиме онлайн (ODR) прекратила работу 20 июля 2025 года и здесь не упоминается.',
          'Tonyte не обязана заявлять о своей готовности участвовать в процедурах потребительского арбитража согласно § 36 VSBG (немецкого Закона об урегулировании потребительских споров), поскольку эта обязанность распространяется только на предприятия, в которых обычно занято более десяти человек.',
        ],
      },
      {
        title: 'Ответственность за содержание',
        iconName: 'alert-triangle',
        paragraphs: [
          'Как поставщик услуг, Akshat Mohiley несёт ответственность за собственный контент Tonyte на данном сайте и в приложении в соответствии с общим законодательством (§ 7(1) DDG). Однако согласно §§ 8–10 DDG отсутствует обязанность отслеживать передаваемую или хранимую информацию третьих лиц (создаваемую пользователями) либо активно выявлять обстоятельства, указывающие на противоправную деятельность. Обязанности по удалению или блокировке использования информации в соответствии с общим законодательством остаются незатронутыми; ответственность в этом отношении возможна только с момента, когда становится известно о конкретном правонарушении. При получении сведений о таком правонарушении соответствующий контент будет незамедлительно удалён.',
        ],
      },
      {
        title: 'Ответственность за ссылки',
        iconName: 'share-2',
        paragraphs: [
          'Сервисы Tonyte могут содержать ссылки на внешние сайты третьих лиц (см. Политику конфиденциальности, раздел 4, о фактически используемых сервисах третьих лиц). Их содержание находится вне контроля Akshat Mohiley, и ответственность за него не принимается. За содержание связанной страницы всегда отвечает соответствующий провайдер или оператор этой страницы. На момент размещения ссылок связанные страницы были проверены на предмет возможных нарушений законодательства; на тот момент противоправного содержания выявлено не было. Постоянный контроль содержания связанных страниц без конкретных указаний на нарушение законодательства является неразумным. При получении сведений о таком нарушении соответствующая ссылка будет незамедлительно удалена.',
        ],
      },
      {
        title: 'Авторское право',
        iconName: 'scroll',
        paragraphs: [
          'Контент, созданный или предоставленный через Tonyte на данном сайте и в приложении, охраняется законодательством Германии об авторском праве. Воспроизведение, переработка, распространение или любое иное использование за пределами, установленными законодательством об авторском праве, требует предварительного письменного согласия. Загрузка и копирование данного контента для частного, некоммерческого использования разрешены. В той мере, в какой контент на этом сайте не был создан оператором, соблюдаются авторские права третьих лиц; пользовательский контент рассматривается отдельно в Условиях использования (раздел «Пользовательский контент»).',
        ],
      },
      {
        title: 'Примечание об области применения',
        iconName: 'info',
        paragraphs: [
          'Настоящий Impressum применяется к сайту Tonyte (tonyte.app и подстраницам), мобильным приложениям Tonyte и Partner Dashboard, в соответствии с определением Сервисов в Политике конфиденциальности, раздел 2.',
        ],
      },
    ],
  },
};
